import { Divider, Icon, List } from "semantic-ui-react";

import type { OpenAPI } from "simplydo/interfaces";

import type { UserMe } from "reducers/user";
import util from "utils/utils";

import CircularProgress from "components/lib/CircularProgress";
import type { SideNavItemProps } from "components/lib/SideNav/SideNavComponent";

type CustomSectionEntryProps = {
  user: UserMe;
  idea: OpenAPI.Assign<
    OpenAPI.Schemas["Idea"],
    OpenAPI.Schemas["AssignIdeaChallenges"],
    OpenAPI.Schemas["AssignIdeaSubmissibilityStatus"]
  >;
  section: { id: string; name: string };
  activeItemId: string | null;
  scrollToItem: (id: string) => void;
  isEditing?: boolean;
};

export const createCustomSectionEntry = ({
  user,
  idea,
  section,
  activeItemId,
  scrollToItem,
  isEditing,
}: CustomSectionEntryProps): SideNavItemProps => {
  const canEdit = util.canEditIdea(user, idea);
  const sectionName = section.name ?? "";
  const submissibility = idea.submissibilityStatus?.detail?.[section.id];

  const missingRequiredFields = submissibility?.incorrectFields.filter(
    (field) => field.type === "required",
  );
  const wordLimitIssues = submissibility?.incorrectFields.filter(
    (field) => field.type === "wordLimit",
  );
  const customIssues = submissibility?.incorrectFields.filter(
    (field) => field.type === "custom",
  );

  const optionalFields =
    (submissibility?.visibleFields || 0) -
    (submissibility?.requiredFields || 0);
  const optionalFieldsCompleted =
    (submissibility?.completedFields || 0) -
    (submissibility?.completedRequiredFields || 0);
  const remainingOptionalFields = optionalFields - optionalFieldsCompleted;
  const totalRemaining =
    (submissibility?.incorrectFields?.length || 0) + remainingOptionalFields;

  let tooltip = "";
  if (missingRequiredFields?.length > 0) {
    tooltip = `${missingRequiredFields.length} required`;
  }
  if (remainingOptionalFields > 0) {
    tooltip =
      tooltip ?
        `${tooltip} (${remainingOptionalFields} optional)`
      : `${remainingOptionalFields} optional`;
  }
  tooltip =
    tooltip ?
      `${tooltip} ${util.pluralise(totalRemaining, "field", "fields", false)} remaining`
    : "All fields completed!";

  const color =
    submissibility ?
      submissibility.completedFields === submissibility.visibleFields ?
        "#28a745"
      : submissibility.incorrectFields?.length > 0 ? "#dc3545"
      : "#007bff"
    : undefined;

  return {
    key: `${sectionName}-${section.id}`,
    type: "link",
    content: sectionName.charAt(0).toUpperCase() + sectionName.slice(1),
    tooltip:
      (
        canEdit &&
        isEditing &&
        submissibility &&
        submissibility.visibleFields > 0
      ) ?
        <div>
          {tooltip}
          {customIssues.length > 0 ?
            <>
              <Divider />
              <b>Issues:</b>
              <div style={{ maxHeight: 300, overflow: "auto", padding: 8 }}>
                <List bulleted>
                  {customIssues?.map((field) => (
                    <List.Item key={field.id}>
                      {field.title}: {field.description}
                    </List.Item>
                  ))}
                </List>
              </div>
            </>
          : null}
          {wordLimitIssues.length > 0 ?
            <>
              <Divider />
              <b>Exceeding word limit:</b>
              <div style={{ maxHeight: 300, overflow: "auto", padding: 8 }}>
                <List bulleted>
                  {wordLimitIssues?.map((field) => (
                    <List.Item key={field.id}>{field.title}</List.Item>
                  ))}
                </List>
              </div>
            </>
          : null}
          {missingRequiredFields.length > 0 ?
            <>
              <Divider />
              <b>
                Required{" "}
                {util.pluralise(
                  missingRequiredFields.length,
                  "field",
                  "fields",
                  false,
                )}{" "}
                remaining:
              </b>
              <div style={{ padding: 8, maxHeight: 300, overflow: "auto" }}>
                <List bulleted>
                  {missingRequiredFields?.map((field) => (
                    <List.Item key={field.id}>
                      {field.title}
                      {field.description ? ` (${field.description})` : ""}
                    </List.Item>
                  ))}
                </List>
              </div>
            </>
          : null}
        </div>
      : null,
    iconLeft: "arrow alternate circle right",
    rightIcon:
      (
        canEdit &&
        isEditing &&
        submissibility &&
        submissibility.visibleFields > 0
      ) ?
        <CircularProgress
          size={16}
          color={color}
          progress={
            submissibility.completedFields / submissibility.visibleFields
          }
          strokeWidth={1.5}
          icon={<Icon name="check" style={{ color }} />}
          iconVisible={
            submissibility.requiredFields ===
              submissibility.completedRequiredFields &&
            (submissibility?.incorrectFields?.length || 0) === 0
          }
        />
      : undefined,
    active: activeItemId === section.id,
    onClick: () => scrollToItem(section.id),
  };
};
